/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstAny,
	ISchemaConstAnyModel,
	SCHEMA_CONST_ANY_VALUE_TYPE,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import {
	IInspectorControls,
	IInspectorControlValueTypes,
	TResolvedInspectorControlValueTypes
} from "../InspectorTypes";
import { getInspectorControls } from "./inspectorControls";
import { IFieldTypeOption } from "../../Fields/FieldType";

export const ControlConstAny_ResolveValueTypes: TResolvedInspectorControlValueTypes = (
	schema: ISchemaConstAny
) => {
	const schemaAllowedTypes = schema.opts.allowedTypes;

	const allowedTypes =
		schemaAllowedTypes !== undefined
			? {
					[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.DATE]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.DATE] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.MAP] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD] || false,
					[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]:
						schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.STRING] || false
			  }
			: {
					[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.DATE]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]: true,
					[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]: true
			  };

	const types: IFieldTypeOption[] = [];

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY,
			icon: "mdi/code-brackets",
			label: "Array"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN,
			icon: "mdi/checkbox-marked-outline",
			label: "Boolean"
		});
	}

	// @todo Enable when date constant will be supported
	// if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.DATE]) {
	// 	types.push({
	// 		value: SCHEMA_CONST_ANY_VALUE_TYPE.DATE,
	// 		icon: "mdi/calendar-clock",
	// 		label: "Date"
	// 	});
	// }

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.MAP,
			icon: "mdi/code-braces",
			label: "Map"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER,
			icon: "mdi/numeric",
			label: "Number"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.STRING,
			icon: "mdi/text-short",
			label: "String"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]) {
		types.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD,
			icon: "mdi/lock",
			label: "Password"
		});
	}

	return types;
};

export const ControlConstAny_ValueTypes: Omit<IInspectorControlValueTypes<ISchemaConstAnyModel>, "model"> = {
	getCurrentType: (model) => {
		return model.valueType;
	},
	setCurrentType: (model, type) => {
		model.schema.setType(model, type as SCHEMA_CONST_ANY_VALUE_TYPE, undefined, true);
	}
};

export const ControlConstAny = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as ISchemaConstAnyModel;

	const valueControls = getInspectorControls(model.value);

	return {
		...valueControls,
		valueTypes: {
			...ControlConstAny_ValueTypes,
			model: model
		},
		dependencyModels: [ model, ...(valueControls.dependencyModels || []) ]
	};
};
